import type { FC } from "react";
import { useForm, useStore } from "@tanstack/react-form";

import { usePatchPatient } from "@/api/patients";
import Edit from "@/assets/icons/edit";
import { FieldInput, FieldMultipleCheckbox } from "@/components/form";
import DialogCustom from "@/components/modals/dialog-custom";
import HealthModalFooter from "@/components/modals/profil-health/health-modal-footer";
import type { HealthUpdateModalProps } from "@/components/modals/profil-health/health-update-modal";
import { Separator } from "@/components/ui/separator";
import type { PatchAllergiesFormData } from "@/schemas/patient";
import { patchAllergiesSchema } from "@/schemas/patient";
import { resetForm } from "@/utils/form";
import { allergiesOptions } from "@/utils/patient";

const AllergiesModal: FC<HealthUpdateModalProps> = ({
  healthModalOpen,
  setHealthModalOpen,
  patient,
}) => {
  const { mutate } = usePatchPatient(patient.id);

  const { allergies, allergiesComment } = patient;

  const { Field, handleSubmit, store } = useForm({
    defaultValues: {
      allergies,
      allergiesComment: allergiesComment ? allergiesComment : null,
    } as PatchAllergiesFormData,
    onSubmit: async ({ value, formApi }) => {
      const values = patchAllergiesSchema.parse(value);
      if (values.allergies === null) {
        values.allergies = [];
      }
      mutate(values, {
        onSuccess: () => {
          setHealthModalOpen("none");
          resetForm(values, formApi);
        },
      });
    },
  });

  const allergiesValues = useStore(store, (state) => state.values.allergies);

  return (
    <DialogCustom
      title="Modifier les informations"
      startIcon={
        <div className="flex p-3 w-12 h-12 justify-center items-center border border-gray-200 rounded-[10px]">
          <Edit className="w-6 h-6" color="black" />
        </div>
      }
      open={healthModalOpen !== "none"}
      onOpenChange={() => setHealthModalOpen("none")}
      className="flex flex-col gap-4"
    >
      <Separator />
      <form onSubmit={handleSubmit} className="flex flex-col gap-4 w-full px-6">
        <FieldMultipleCheckbox
          Field={Field}
          schema={patchAllergiesSchema}
          name="allergies"
          options={allergiesOptions}
          validators={{
            onChange: patchAllergiesSchema.shape.allergies,
          }}
        />
        {allergiesValues?.includes("OTHER") ? (
          <FieldInput
            Field={Field}
            schema={patchAllergiesSchema}
            name="allergiesComment"
            placeholder="Ajouter un commentaire"
            className="ml-[50%]"
            validators={{
              onChange: patchAllergiesSchema.shape.allergiesComment,
            }}
          />
        ) : null}
        <input type="submit" className="hidden" />
      </form>
      <Separator />
      <HealthModalFooter
        setHealthModalOpen={setHealthModalOpen}
        handleSubmit={handleSubmit}
      />
    </DialogCustom>
  );
};

export default AllergiesModal;
